import { FC, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { documentoService } from '@/services/documentoService'
import type {
  ConteudoPublicado,
  ConteudoPublicadoRequest,
  NovaVersaoConteudoRequest,
  PerfilVisibilidade,
  TipoConteudoPublicado,
} from '@/types/conteudo'
import { PERFIS_VISIBILIDADE, TIPOS_CONTEUDO } from '@/types/conteudo'

/**
 * Onda 5 (AN-11 §1.3, Q-AN11-4): Guia comercial, Checklist documental e Roteiro de reunião são
 * material do back-office — o servidor recusa perfil CONSULTOR (422 `PERFIL_NAO_PERMITIDO`).
 * A opção nem aparece; o PDF que o consultor vê é o informativo da aba do produto.
 */
const PERFIS_PUBLICAVEIS = (Object.keys(PERFIS_VISIBILIDADE) as PerfilVisibilidade[]).filter(
  (p) => p !== 'CONSULTOR'
)
const PERFIS_PADRAO: PerfilVisibilidade[] = ['GERENTE', 'OPERADOR']

const MENSAGEM_PERFIL_NAO_PERMITIDO =
  'Este material não pode ser visível ao consultor. O PDF do consultor é o informativo publicado na aba do produto.'
import type { Produto } from '@/types/produto'
import { tokens } from '@/theme'

/**
 * `tipoEntidade` que o serviço de documentos reconhece como material da plataforma: o
 * arquivo pode ser lido por consultor de qualquer tenant (o registro em produtos já
 * filtrou perfil e tenant). Só o ADMIN consegue subir com esse marcador.
 */
export const TIPO_ENTIDADE_CONTEUDO_PUBLICADO = 'CONTEUDO_PUBLICADO'

interface Props {
  aberto: boolean
  /** Com `anterior`, o diálogo publica uma NOVA VERSÃO (mesmo tipo/produto/perfis, `versao + 1`). */
  anterior: ConteudoPublicado | null
  produtos: Produto[]
  onFechar: () => void
  onPublicar: (request: ConteudoPublicadoRequest) => Promise<void>
  onNovaVersao: (anteriorId: string, request: NovaVersaoConteudoRequest) => Promise<void>
}

const hojeIso = () => new Date().toISOString().slice(0, 10)

/**
 * "+ Publicar material" (A12, Onda 7.2): PDF sobe pelo upload existente do serviço de
 * documentos (auditado, com antivírus) → `documentoId` → registro tipado em produtos.
 */
const PublicarMaterialDialog: FC<Props> = ({
  aberto,
  anterior,
  produtos,
  onFechar,
  onPublicar,
  onNovaVersao,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [titulo, setTitulo] = useState('')
  const [tipo, setTipo] = useState<TipoConteudoPublicado>('GUIA_COMERCIAL')
  const [produtoId, setProdutoId] = useState<string>('')
  const [perfis, setPerfis] = useState<PerfilVisibilidade[]>(PERFIS_PADRAO)
  const [vigenciaInicio, setVigenciaInicio] = useState(hojeIso())
  const [vigenciaFim, setVigenciaFim] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (!aberto) return
    setArquivo(null)
    setErro(null)
    setTitulo(anterior?.titulo ?? '')
    setTipo(anterior?.tipo ?? 'GUIA_COMERCIAL')
    setProdutoId(anterior?.produtoId ?? '')
    setPerfis(anterior ? anterior.perfis.filter((p) => p !== 'CONSULTOR') : PERFIS_PADRAO)
    setVigenciaInicio(hojeIso())
    setVigenciaFim('')
  }, [aberto, anterior])

  const alternarPerfil = (perfil: PerfilVisibilidade) =>
    setPerfis((atual) =>
      atual.includes(perfil) ? atual.filter((p) => p !== perfil) : [...atual, perfil]
    )

  const valido =
    !!arquivo && titulo.trim().length > 0 && perfis.length > 0 && vigenciaInicio.length === 10

  const publicar = async () => {
    if (!arquivo) return
    setEnviando(true)
    setErro(null)
    try {
      const enviado = await documentoService.upload(
        arquivo,
        'MANUAL',
        tipo,
        TIPO_ENTIDADE_CONTEUDO_PUBLICADO
      )
      if (anterior) {
        await onNovaVersao(anterior.id, {
          documentoId: enviado.id,
          titulo: titulo.trim(),
          vigenciaInicio,
          vigenciaFim: vigenciaFim || null,
        })
      } else {
        await onPublicar({
          produtoId: produtoId || null,
          tipo,
          titulo: titulo.trim(),
          documentoId: enviado.id,
          vigenciaInicio,
          vigenciaFim: vigenciaFim || null,
          perfis,
        })
      }
      onFechar()
    } catch (e) {
      const codigo = (e as { data?: { errorCode?: unknown } } | null)?.data?.errorCode
      const m = (e as { message?: unknown } | null)?.message
      setErro(
        codigo === 'PERFIL_NAO_PERMITIDO'
          ? MENSAGEM_PERFIL_NAO_PERMITIDO
          : typeof m === 'string' && m
            ? m
            : 'Não foi possível publicar o material'
      )
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Dialog open={aberto} onClose={enviando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle>
        {anterior ? `Nova versão — v${anterior.versao + 1}` : 'Publicar material'}
      </DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {erro && <Alert severity="error">{erro}</Alert>}
        {anterior && (
          <Alert severity="info">
            A versão {anterior.versao} deixa de valer assim que a nova for publicada. Tipo,
            produto e visibilidade são herdados.
          </Alert>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          hidden
          aria-label="Arquivo PDF do material"
          onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={enviando}
          >
            Escolher PDF
          </Button>
          <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
            {arquivo ? arquivo.name : 'Nenhum arquivo escolhido'}
          </Typography>
        </Stack>

        <TextField
          label="Título"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          required
          inputProps={{ 'aria-label': 'Título do material', maxLength: 200 }}
        />

        {!anterior && (
          <>
            <FormControl>
              <InputLabel id="material-tipo">Tipo</InputLabel>
              <Select
                labelId="material-tipo"
                label="Tipo"
                value={tipo}
                onChange={(e) => setTipo(e.target.value as TipoConteudoPublicado)}
              >
                {(Object.keys(TIPOS_CONTEUDO) as TipoConteudoPublicado[]).map((t) => (
                  <MenuItem key={t} value={t}>
                    {TIPOS_CONTEUDO[t]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel id="material-produto">Produto</InputLabel>
              <Select
                labelId="material-produto"
                label="Produto"
                value={produtoId}
                onChange={(e) => setProdutoId(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">Todos os produtos</MenuItem>
                {produtos.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl component="fieldset">
              <FormLabel component="legend">Visibilidade</FormLabel>
              <FormGroup row>
                {PERFIS_PUBLICAVEIS.map((perfil) => (
                  <FormControlLabel
                    key={perfil}
                    control={
                      <Checkbox
                        checked={perfis.includes(perfil)}
                        onChange={() => alternarPerfil(perfil)}
                      />
                    }
                    label={PERFIS_VISIBILIDADE[perfil]}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </>
        )}

        <Stack direction="row" spacing={2}>
          <TextField
            label="Vigência a partir de"
            type="date"
            value={vigenciaInicio}
            onChange={(e) => setVigenciaInicio(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            fullWidth
          />
          <TextField
            label="Vigência até"
            type="date"
            value={vigenciaFim}
            onChange={(e) => setVigenciaFim(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Em branco = sem prazo"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={enviando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={publicar} disabled={!valido || enviando}>
          {enviando ? 'Publicando…' : 'Publicar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PublicarMaterialDialog
